/* Dateien öffnen, herunterladen, hochladen, entfernen.
 *
 * Alle Dateien liegen im geschützten Speicher und sind nur über zeitlich
 * begrenzte signierte Links erreichbar. Ein Dateiobjekt hat entweder ein
 * `path` (Speicher) oder ein `url` (Altbestand mit eingebetteten Daten).
 */

import { ref } from 'vue';
import { signedUrl, uploadFile, removeFile } from '../supabase/storage.js';

export function useFileActions() {
  const busy = ref(false);
  const error = ref(null);

  async function hrefFor(file) {
    if (!file) return null;
    if (file.path) return await signedUrl(file.path);
    return file.url || file.data || null;
  }

  async function open(file) {
    const href = await hrefFor(file);
    if (href) window.open(href, '_blank', 'noopener');
  }

  /**
   * Herunterladen statt Anzeigen. Der Umweg über einen Blob ist nötig, weil
   * der signierte Link auf einer fremden Adresse liegt — ein einfaches
   * download-Attribut würde dort ignoriert und die Datei nur geöffnet.
   */
  async function download(file) {
    const href = await hrefFor(file);
    if (!href) return;
    const name = file.filename || file.name || 'download';
    try {
      const res = await fetch(href);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      /* Notnagel: wenigstens öffnen, dann kann der Nutzer selbst sichern */
      window.open(href, '_blank', 'noopener');
    }
  }

  async function upload(file, folder) {
    busy.value = true;
    error.value = null;
    try {
      const path = await uploadFile(file, folder);
      return { filename: file.name, path };
    } catch (e) {
      error.value = e.message || 'uploadFailed';
      throw e;
    } finally {
      busy.value = false;
    }
  }

  async function remove(file) {
    if (file && file.path) await removeFile(file.path);
  }

  return { busy, error, hrefFor, open, download, upload, remove };
}
